import { habilidadesDe } from './habilidades';
import { forcaBase } from './tatica';
import type { Clube, ConcentracaoAtaque, Escalacao, Estilo, Jogador, Marcacao, Papel, Passe, Posicao, Pressao } from './tipos';

/**
 * Modelo tático do motor de partida. Cada escolha do técnico (mentalidade, passe, pressão, agressividade, onde atacar e as
 * funções individuais) tem efeito próprio em três coisas: a posse de bola, a força do ataque e a solidez da defesa, e o
 * confronto entre os dois estilos pesa mais que cada escolha sozinha:
 *  - pressão alta sufoca o passe curto de um meio-campo fraco, mas abre espaço para a bola longa nas costas da defesa;
 *  - bloco baixo anula o passe curto e o jogo aéreo sem referência, mas cede a posse;
 *  - quem ataca com tudo fica exposto ao contra-ataque de um time rápido;
 *  - pressionar e marcar forte gasta o fôlego dos jogadores minuto a minuto (a energia em campo cai e a força cai junto), e
 *    pressão alta com o time cansado desmorona a defesa no fim do jogo.
 * Tudo aqui é função pura do estado do jogo (sem sorteio), para o jogo ser reproduzível minuto a minuto no modo online.
 */

export const ORDEM_ESTILO: Record<Estilo, number> = { retranca: -2, defensivo: -1, equilibrado: 0, ofensivo: 1, total: 2 };
export const LISTA_ESTILOS: readonly Estilo[] = ['retranca', 'defensivo', 'equilibrado', 'ofensivo', 'total'];
export const LISTA_PASSES: readonly Passe[] = ['curto', 'misto', 'longo'];
export const LISTA_PRESSOES: readonly Pressao[] = ['baixa', 'media', 'alta'];

export const ROTULO_ESTILO: Record<Estilo, string> = {
  retranca: 'Super defensivo (retranca)',
  defensivo: 'Defensivo (contra-ataque)',
  equilibrado: 'Equilibrado',
  ofensivo: 'Ofensivo (propositivo)',
  total: 'Super ofensivo (tudo ou nada)',
};
export const ROTULO_PASSE: Record<Passe, string> = { curto: 'Curto (posse de bola)', misto: 'Misto', longo: 'Longo (ligação direta)' };
export const ROTULO_PRESSAO: Record<Pressao, string> = { baixa: 'Baixa (bloco recuado)', media: 'Média', alta: 'Alta (sufoca a saída de bola)' };
export const DICA_ESTILO: Record<Estilo, string> = {
  retranca: 'Linhas baixas e chutão. Sofre menos, mas quase não ataca e cede a bola.',
  defensivo: 'Espera e sai em velocidade. Bom contra quem se expõe, ruim contra quem fecha.',
  equilibrado: 'Sem riscos nem vantagens.',
  ofensivo: 'Domina o território; a defesa joga adiantada e abre espaço atrás.',
  total: 'Todo mundo ao ataque. Cria muito, mas leva contra-ataque e cansa rápido.',
};
export const DICA_PASSE: Record<Passe, string> = {
  curto: 'Mais posse, mas sofre contra pressão alta se o meio-campo for fraco e perde força contra bloco baixo.',
  misto: 'Varia conforme o jogo, sem pontos fortes nem fracos.',
  longo: 'Pula o meio-campo. Funciona contra defesa adiantada e com atacantes rápidos ou um homem de referência; contra bloco baixo é ruim.',
};
export const DICA_PRESSAO: Record<Pressao, string> = {
  baixa: 'Espera no próprio campo: defesa mais firme, poupa energia, cede a posse.',
  media: 'Marcação a partir do meio-campo.',
  alta: 'Rouba a bola perto da área rival, mas gasta muita energia e, cansado, a defesa desmorona no fim.',
};

// ---- Tabelas de efeito -----------------------------------------------------------------------------------

const EST_ATQ: Record<Estilo, number> = { retranca: 0.86, defensivo: 0.93, equilibrado: 1, ofensivo: 1.07, total: 1.14 };
const EST_DEF: Record<Estilo, number> = { retranca: 1.13, defensivo: 1.07, equilibrado: 1, ofensivo: 0.93, total: 0.86 };
const EST_POSSE: Record<Estilo, number> = { retranca: 0.93, defensivo: 0.97, equilibrado: 1, ofensivo: 1.03, total: 1.06 };
const EST_DRENO: Record<Estilo, number> = { retranca: 0.9, defensivo: 0.95, equilibrado: 1, ofensivo: 1.08, total: 1.25 };
const PASSE_POSSE: Record<Passe, number> = { curto: 1.05, misto: 1, longo: 0.95 };
const PASSE_ATQ: Record<Passe, number> = { curto: 0.975, misto: 1, longo: 1.035 };
const PASSE_DRENO: Record<Passe, number> = { curto: 0.96, misto: 1, longo: 1.03 };
const MARC_DEF: Record<Marcacao, number> = { leve: 0.985, normal: 1, forte: 1.03 };
const MARC_DRENO: Record<Marcacao, number> = { leve: 0.94, normal: 1, forte: 1.18 };
const PRESSAO_DRENO: Record<Pressao, number> = { baixa: 0.75, media: 1, alta: 1.9 };
/**
 * Quanto as escolhas táticas pesam no sorteio de cada minuto. Os multiplicadores abaixo são pequenos (poucos por cento) e o
 * motor os dilui (a diferença de força é dividida por 8); elevados a este expoente eles viram diferença que o técnico sente.
 */
export const PESO_TATICO = 2.3;
const POS_DRENO: Record<Posicao, number> = { G: 0.12, Z: 0.9, L: 1.12, M: 1.1, A: 1 };
/** Perda de energia por minuto de um jogador de linha com tática neutra (≈ 22 pontos em 90 minutos, como era o custo fixo). */
const DRENO_BASE = 0.245;

const limitar = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v));

/** Quanto a pressão ainda funciona com o time nesta forma física (0 = acabado, 1 = inteiro). */
export function eficienciaPressao(forma: number): number {
  return limitar((forma - 0.6) / 0.3, 0, 1);
}

// ---- Funções individuais ---------------------------------------------------------------------------------

interface DefPapel {
  nome: string;
  posicao: Posicao;
  descricao: string;
  /** características (índices de CARACTERISTICAS) que o fazem render mais na função */
  tracos: number[];
  /** habilidade que mais pesa na aptidão */
  hab?: 'vel' | 'tec' | 'pas' | 'arm' | 'fin' | 'des' | 'gol';
  /** multiplicador do desgaste de quem joga na função */
  dreno: number;
  /** finalização: peso do jogador ao ser escolhido para chutar */
  finalizador?: number;
  ef: Partial<EfeitoPapeis>;
}

export interface EfeitoPapeis {
  posse: number;
  ataque: number;
  defesa: number;
  /** ficar exposto ao contra-ataque (negativo = cobre os espaços) */
  exposicao: number;
  /** capacidade de sair em contra-ataque */
  contra: number;
  /** ajuda a escapar da pressão do adversário */
  resPressao: number;
  /** jogo aéreo do ataque */
  aereo: number;
  /** bônus quando o time ataca pelos lados */
  lados: number;
}

const ZERO_EFEITO = (): EfeitoPapeis => ({ posse: 0, ataque: 0, defesa: 0, exposicao: 0, contra: 0, resPressao: 0, aereo: 0, lados: 0 });
/** Efeito de quem não tem função nenhuma em campo (compartilhado: não alterar). */
const SEM_EFEITOS: EfeitoPapeis = Object.freeze(ZERO_EFEITO());

export const PAPEIS: Record<Papel, DefPapel> = {
  libero: { nome: 'Goleiro líbero', posicao: 'G', descricao: 'Joga adiantado e cobre as costas da defesa; ajuda na saída de bola.', tracos: [3, 0], hab: 'gol', dreno: 0.9, ef: { defesa: 0.012, exposicao: -0.04, resPressao: 0.08 } },
  rebatedor: { nome: 'Zagueiro rebatedor', posicao: 'Z', descricao: 'Não complica: ao roubar a bola, afasta. Defende mais, constrói menos.', tracos: [10, 7, 0], hab: 'des', dreno: 0.95, ef: { defesa: 0.03, posse: -0.02, resPressao: 0.25 } },
  construtor: { nome: 'Zagueiro construtor', posicao: 'Z', descricao: 'Inicia as jogadas com lançamentos e passes entrelinhas. Exige bom passe.', tracos: [11, 4], hab: 'pas', dreno: 1, ef: { posse: 0.025, defesa: -0.012, resPressao: 0.18 } },
  'lateral-apoiador': { nome: 'Lateral apoiador', posicao: 'L', descricao: 'Sobe pela linha e cruza. Dá volume pelos lados, mas deixa o flanco aberto.', tracos: [6, 13, 12], hab: 'vel', dreno: 1.25, ef: { ataque: 0.014, lados: 0.02, defesa: -0.012, exposicao: 0.01 } },
  'lateral-invertido': { nome: 'Lateral invertido', posicao: 'L', descricao: 'Corta para o meio e age como volante extra: mais posse, menos largura.', tracos: [11, 8], hab: 'tec', dreno: 1.1, ef: { posse: 0.018, ataque: 0.006, lados: -0.02, defesa: -0.004, resPressao: 0.1 } },
  'volante-fixo': { nome: 'Primeiro volante', posicao: 'M', descricao: 'Fica à frente dos zagueiros e não ataca: protege a defesa e cobre o contra-ataque.', tracos: [7, 10, 0], hab: 'des', dreno: 0.85, ef: { defesa: 0.03, ataque: -0.01, exposicao: -0.035, posse: -0.004 } },
  regista: { nome: 'Regista (construtor recuado)', posicao: 'M', descricao: 'Dita o ritmo lá de trás. Brilha com passe curto; exige visão e passe.', tracos: [11, 4], hab: 'pas', dreno: 0.9, ef: { posse: 0.035, resPressao: 0.3, defesa: -0.01, ataque: 0.008 } },
  'box-to-box': { nome: 'Meia área a área', posicao: 'M', descricao: 'Corre o campo todo: ajuda na defesa e aparece na área. Gasta muita energia.', tracos: [12, 13], hab: 'des', dreno: 1.38, ef: { posse: 0.008, ataque: 0.012, defesa: 0.012 } },
  mezala: { nome: 'Mezzala', posicao: 'M', descricao: 'Meia que ataca os espaços entre a ponta e a área; rende mais com ataques pelo meio.', tracos: [8, 4, 9], hab: 'arm', dreno: 1.1, ef: { ataque: 0.022, defesa: -0.008, posse: 0.006 } },
  'ponta-construtor': { nome: 'Ponta construtor', posicao: 'A', descricao: 'Flutua para o meio e dá assistências; ajuda a escapar da pressão.', tracos: [4, 8, 11], hab: 'arm', dreno: 1.1, ef: { posse: 0.014, ataque: 0.012, resPressao: 0.1, exposicao: 0.008 } },
  extremo: { nome: 'Extremo clássico', posicao: 'A', descricao: 'Cola na linha, vai ao fundo e cruza; sai bem em contra-ataques. Precisa de velocidade.', tracos: [13, 6, 8], hab: 'vel', dreno: 1.2, ef: { ataque: 0.01, lados: 0.03, contra: 0.12 } },
  'atacante-interior': { nome: 'Atacante interior', posicao: 'A', descricao: 'Corta para dentro e finaliza; vira o principal chutador do time.', tracos: [9, 8], hab: 'fin', dreno: 1.05, finalizador: 1.35, ef: { ataque: 0.016, lados: -0.01 } },
  falso9: { nome: 'Falso 9', posicao: 'A', descricao: 'O centroavante recua para o meio, puxa o zagueiro e abre espaço. Precisa de mais um atacante.', tracos: [4, 11, 8], hab: 'tec', dreno: 1.05, ef: { posse: 0.015, ataque: 0.004 } },
  referencia: { nome: 'Homem de referência', posicao: 'A', descricao: 'Fixo na área: pivô e jogo aéreo. Casa com passe longo e cruzamentos.', tracos: [5, 9], hab: 'fin', dreno: 0.95, ef: { ataque: 0.01, aereo: 0.3, posse: -0.008 } },
  oportunista: { nome: 'Atacante oportunista', posicao: 'A', descricao: 'Fica na linha do último defensor esperando o passe em profundidade; pouco participa.', tracos: [9, 13], hab: 'fin', dreno: 0.9, finalizador: 1.5, ef: { ataque: 0.012, contra: 0.18, posse: -0.018 } },
};

/** Funções que fazem sentido na posição dada (para a tela de escalação). */
export function papeisDaPosicao(p: Posicao): Papel[] {
  return (Object.keys(PAPEIS) as Papel[]).filter((k) => PAPEIS[k].posicao === p);
}

/** Quão bem o jogador cumpre a função (0,6 a 1,4): características e habilidade da função aumentam o efeito. */
export function aptidaoPapel(papel: Papel, j: Jogador): number {
  const d = PAPEIS[papel];
  let apt = 0.7;
  for (const c of j.caracteristicas) if (d.tracos.includes(c)) apt += 0.2;
  const hv = (d.hab ? habilidadesDe(j)[d.hab] : j.overall) / 100;
  apt += limitar((hv - 0.5) * 0.9, -0.1, 0.4);
  return limitar(apt, 0.6, 1.4);
}

export interface PapelAtivo {
  papel: Papel;
  jogador: Jogador;
  aptidao: number;
}

/** Soma os efeitos das funções que estão em campo (cada uma na posição certa), com limite para não empilhar bônus. */
export function efeitosDosPapeis(ativos: PapelAtivo[], ataque: ConcentracaoAtaque, passe: Passe): EfeitoPapeis {
  if (ativos.length === 0) return SEM_EFEITOS;
  const t = ZERO_EFEITO();
  for (const a of ativos) {
    const ef = PAPEIS[a.papel].ef;
    for (const k of Object.keys(ef) as (keyof EfeitoPapeis)[]) t[k] += (ef[k] ?? 0) * a.aptidao;
    if (a.papel === 'mezala' && ataque !== 'lados') t.ataque += 0.01 * a.aptidao;
    if (a.papel === 'regista' && passe === 'curto') t.posse += 0.02 * a.aptidao;
    if (a.papel === 'referencia' && passe === 'longo') t.ataque += 0.02 * a.aptidao;
  }
  t.posse = limitar(t.posse, -0.08, 0.08);
  t.ataque = limitar(t.ataque, -0.07, 0.07);
  t.defesa = limitar(t.defesa, -0.07, 0.07);
  t.exposicao = limitar(t.exposicao, -0.08, 0.06);
  t.contra = limitar(t.contra, 0, 0.4);
  t.resPressao = limitar(t.resPressao, 0, 0.6);
  t.aereo = limitar(t.aereo, 0, 0.5);
  t.lados = limitar(t.lados, -0.04, 0.06);
  return t;
}

/** Multiplicador de desgaste de quem joga na função. */
export const drenoDoPapel = (p: Papel | undefined): number => (p ? PAPEIS[p].dreno : 1);
/** Peso extra de quem finaliza mais por causa da função. */
export const pesoFinalizador = (p: Papel | undefined): number => (p ? (PAPEIS[p].finalizador ?? 1) : 1);

// ---- Perfil e condição de uma equipe ----------------------------------------------------------------------

export interface PerfilTatico {
  estilo: Estilo;
  passe: Passe;
  pressao: Pressao;
  marcacao: Marcacao;
  ataque: ConcentracaoAtaque;
}

export interface CondicaoEquipe {
  /** média da energia dos jogadores de linha em campo (0..1) */
  forma: number;
  /** força do meio-campo (0..10, a mesma escala de forcaBase) */
  meio: number;
  /** velocidade média dos atacantes em campo (0..1) */
  vel: number;
  /** jogo aéreo do ataque e da defesa (0..1) */
  aereoAtq: number;
  aereoDef: number;
  papeis: EfeitoPapeis;
}

export function condicaoDe(campo: readonly Jogador[], slotDe: (j: Jogador) => Posicao, energiaDe: (j: Jogador) => number, meio: number, papeis: EfeitoPapeis): CondicaoEquipe {
  let somaEn = 0;
  let nEn = 0;
  let somaVel = 0;
  let nVel = 0;
  let cabAtq = 0;
  let cabDef = 0;
  for (const j of campo) {
    const slot = slotDe(j);
    if (slot !== 'G') {
      somaEn += energiaDe(j);
      nEn++;
    }
    if (slot === 'A') {
      somaVel += habilidadesDe(j).vel;
      nVel++;
    }
    const [c1, c2] = j.caracteristicas;
    if ((slot === 'A' || slot === 'M') && (c1 === 5 || c2 === 5)) cabAtq++;
    if (slot === 'Z' && (c1 === 5 || c1 === 10 || c1 === 0 || c2 === 5 || c2 === 10 || c2 === 0)) cabDef++;
  }
  return {
    forma: nEn ? somaEn / nEn / 100 : 0.8,
    meio,
    vel: nVel ? somaVel / nVel / 100 : 0.6,
    aereoAtq: limitar(0.35 + 0.18 * cabAtq + papeis.aereo, 0, 1),
    aereoDef: limitar(0.35 + 0.17 * cabDef, 0, 1),
    papeis,
  };
}

// ---- Efeitos do confronto ---------------------------------------------------------------------------------

export interface ModsOfensivos {
  /** multiplica o peso da posse de bola de quem ataca */
  posse: number;
  /** multiplica a força do ataque */
  ataque: number;
}

/** O que a tática de `a` rende contra a de `b`: posse e ataque. */
export function modsOfensivos(a: PerfilTatico, ca: CondicaoEquipe, b: PerfilTatico, cb: CondicaoEquipe): ModsOfensivos {
  const pe = eficienciaPressao(ca.forma);
  let posse = EST_POSSE[a.estilo] * PASSE_POSSE[a.passe] * (1 + ca.papeis.posse);
  if (a.pressao === 'alta') posse *= 1 + 0.08 * pe;
  else if (a.pressao === 'baixa') posse *= 0.95;
  let ataque = EST_ATQ[a.estilo] * PASSE_ATQ[a.passe] * (1 + ca.papeis.ataque);
  if (a.ataque === 'lados') ataque *= 1 + ca.papeis.lados;
  else if (a.ataque === 'equilibrado') ataque *= 1 + ca.papeis.lados * 0.4;

  // pressão do adversário
  const peB = eficienciaPressao(cb.forma);
  if (b.pressao === 'alta') {
    const sens = limitar((6.8 - ca.meio) / 2.2 - ca.papeis.resPressao, 0.15, 1.3);
    const peso = a.passe === 'curto' ? 1 : a.passe === 'misto' ? 0.45 : 0.05;
    const perda = 0.14 * peB * sens * peso;
    posse *= 1 - perda;
    ataque *= 1 - 0.45 * perda;
    if (a.passe === 'longo') ataque *= 1 + 0.13 * ca.vel + 0.1 * (1 - peB);
    else if (a.passe === 'misto') ataque *= 1 + 0.035 * ca.vel;
    if (a.ataque === 'meio') ataque *= 1 - 0.02 * peB;
    else if (a.ataque === 'lados') ataque *= 1 + 0.015 * peB;
  } else if (b.pressao === 'baixa') {
    if (a.passe === 'curto') {
      posse *= 1.04;
      ataque *= 1.03;
    } else if (a.passe === 'longo') {
      ataque *= limitar(1 - 0.09 + 0.14 * ca.aereoAtq - 0.08 * (cb.aereoDef - 0.5), 0.85, 1.08);
    }
    if (a.ataque === 'lados') ataque *= 0.985;
  }

  // agressividade do adversário: marcação forte quebra o ritmo de quem tem a bola
  if (b.marcacao === 'forte') posse *= 0.97;
  else if (b.marcacao === 'leve') posse *= 1.015;

  // bloco baixo e fechado do adversário: quem ataca com tudo encontra pouco espaço
  if (b.pressao === 'baixa' && ORDEM_ESTILO[b.estilo] <= -1) ataque *= 1 - 0.03 * Math.max(0, ORDEM_ESTILO[a.estilo]);

  // exposição do adversário ao contra-ataque
  const exposicao = Math.max(0, ORDEM_ESTILO[b.estilo]) * 0.035 + (b.pressao === 'alta' ? 0.03 + 0.06 * (1 - peB) : 0) + cb.papeis.exposicao;
  if (exposicao > 0) {
    const contra = (ORDEM_ESTILO[a.estilo] <= 0 ? 1 : 0.55) * (a.passe === 'longo' ? 1 : 0.7) * (0.6 + 0.8 * ca.vel) * (1 + ca.papeis.contra);
    ataque *= 1 + 2 * exposicao * contra;
  } else if (exposicao < 0) {
    ataque *= 1 + 0.5 * exposicao; // defesa que cobre os espaços tira o contra-ataque
  }
  return { posse: Math.pow(posse, PESO_TATICO), ataque: Math.pow(ataque, PESO_TATICO) };
}

/** Multiplicador da solidez defensiva de `p` (mentalidade, agressividade, altura do bloco, cansaço e funções). */
export function multiplicadorDefesa(p: PerfilTatico, c: CondicaoEquipe): number {
  let d = EST_DEF[p.estilo] * MARC_DEF[p.marcacao] * (1 + c.papeis.defesa);
  if (p.pressao === 'alta') d *= 0.98 - 0.09 * (1 - eficienciaPressao(c.forma));
  else if (p.pressao === 'baixa') d *= 1.035;
  return Math.pow(d, PESO_TATICO);
}

// ---- Desgaste ---------------------------------------------------------------------------------------------

/** Quanto o jeito de jogar cansa a mais que o neutro (0 = igual; 1 = o dobro do desgaste). */
export function custoFisico(p: PerfilTatico): number {
  return PRESSAO_DRENO[p.pressao] * EST_DRENO[p.estilo] * MARC_DRENO[p.marcacao] * PASSE_DRENO[p.passe] - 1;
}

/** Energia que o jogador perde em um minuto, de acordo com a tática, a posição, a função, a idade e a resistência dele. */
export function drenoPorMinuto(p: PerfilTatico, j: Jogador, slot: Posicao, papel?: Papel): number {
  let d = DRENO_BASE * PRESSAO_DRENO[p.pressao] * EST_DRENO[p.estilo] * MARC_DRENO[p.marcacao] * PASSE_DRENO[p.passe] * POS_DRENO[slot] * drenoDoPapel(papel);
  if (j.caracteristicas.includes(12)) d *= 0.85; // Resistência
  if (j.idade >= 33) d *= 1.12;
  else if (j.idade <= 21) d *= 0.95;
  return d;
}

// ---- Técnico da CPU ---------------------------------------------------------------------------------------

export interface Arquetipo extends PerfilTatico {
  id: string;
  nome: string;
}

export const ARQUETIPOS: Arquetipo[] = [
  { id: 'posse', nome: 'jogo de posse', estilo: 'ofensivo', passe: 'curto', pressao: 'media', marcacao: 'leve', ataque: 'meio' },
  { id: 'pressao', nome: 'pressão alta', estilo: 'ofensivo', passe: 'misto', pressao: 'alta', marcacao: 'normal', ataque: 'meio' },
  { id: 'contra', nome: 'bloco baixo e contra-ataque', estilo: 'defensivo', passe: 'longo', pressao: 'baixa', marcacao: 'leve', ataque: 'lados' },
  { id: 'direto', nome: 'jogo direto', estilo: 'equilibrado', passe: 'longo', pressao: 'media', marcacao: 'leve', ataque: 'lados' },
  { id: 'equilibrado', nome: 'jogo equilibrado', estilo: 'equilibrado', passe: 'misto', pressao: 'media', marcacao: 'leve', ataque: 'equilibrado' },
  { id: 'retranca', nome: 'retranca', estilo: 'retranca', passe: 'longo', pressao: 'baixa', marcacao: 'forte', ataque: 'lados' },
  { id: 'total', nome: 'ataque total', estilo: 'total', passe: 'curto', pressao: 'alta', marcacao: 'normal', ataque: 'meio' },
];

const hashTexto = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h;
};

/** Jeito de jogar habitual do clube (a "escola" do técnico): fixo por clube, mais ofensivo nos grandes e mais cauteloso nos pequenos. */
export function arquetipoHabitual(clube: Clube): Arquetipo {
  const r = clube.reputacao ?? 3;
  const pesos: Record<string, number> = {
    posse: 1 + Math.max(0, r - 2) * 0.9,
    pressao: 1 + Math.max(0, r - 2) * 0.7,
    contra: 1.1 + Math.max(0, 3 - r) * 0.6,
    direto: 1.2 + Math.max(0, 3 - r) * 0.5,
    equilibrado: 1.2,
    retranca: 0.2 + Math.max(0, 3 - r) * 0.35,
    total: 0.3 + Math.max(0, r - 3) * 0.5,
  };
  const total = ARQUETIPOS.reduce((s, a) => s + (pesos[a.id] ?? 1), 0);
  let x = ((hashTexto(clube.sigla + clube.nome) % 10000) / 10000) * total;
  for (const a of ARQUETIPOS) {
    x -= pesos[a.id] ?? 1;
    if (x < 0) return a;
  }
  return ARQUETIPOS[4] as Arquetipo;
}

const PERFIL_NEUTRO: PerfilTatico = { estilo: 'equilibrado', passe: 'misto', pressao: 'media', marcacao: 'normal', ataque: 'equilibrado' };

/** Condição estimada de uma escalação, para comparar estilos antes do jogo. */
export function condicaoDaEscalacao(esc: Escalacao): CondicaoEquipe {
  const slots = new Map<number, Posicao>(esc.titulares.map((j, i) => [j.id, esc.slots?.[i] ?? j.posicao]));
  const slotDe = (j: Jogador): Posicao => slots.get(j.id) ?? j.posicao;
  const meios = esc.titulares.filter((j) => slotDe(j) === 'M').map((j) => forcaBase(j)).sort((a, b) => b - a).slice(0, 5);
  const meio = meios.length ? meios.reduce((s, x) => s + x, 0) / 5 : 0.1;
  return condicaoDe(esc.titulares, slotDe, (j) => j.energia, meio, SEM_EFEITOS);
}

/** Quanto mais chances (em log) o perfil `a` cria que o `b`, comparando posse, ataque e defesa dos dois. */
export function vantagemTatica(a: PerfilTatico, ca: CondicaoEquipe, b: PerfilTatico, cb: CondicaoEquipe): number {
  const ma = modsOfensivos(a, ca, b, cb);
  const mb = modsOfensivos(b, cb, a, ca);
  const criaA = ma.posse * ma.ataque / multiplicadorDefesa(b, cb);
  const criaB = mb.posse * mb.ataque / multiplicadorDefesa(a, ca);
  // quem pressiona e se expõe cansa mais (e o time já cansado sente mais): o custo físico entra na conta
  const pesoFisico = (c: CondicaoEquipe): number => PESO_CUSTO_FISICO * (1 + 3 * Math.max(0, 0.9 - c.forma));
  return Math.log(criaA / criaB) - pesoFisico(ca) * custoFisico(a) + pesoFisico(cb) * custoFisico(b);
}

/** Peso do cansaço na estimativa de vantagem (ajustado por regressão contra as simulações: scripts/calibrar-taticas.ts, seção 7). */
const PESO_CUSTO_FISICO = 0.28;

export interface ContextoCpu {
  /** o adversário joga assim (o técnico da CPU lê isso com alguma chance de errar) */
  adversario: PerfilTatico;
  /** diferença de força entre o time da CPU e o adversário (escala 0..10) */
  diferencaForca: number;
  casa: boolean;
  /** placar do ponto de vista da CPU e minuto do jogo (0 = antes do início) */
  saldo: number;
  minuto: number;
  /** arquétipo em uso agora (a CPU só troca se outro for claramente melhor) */
  atualId?: string;
}

/**
 * Técnico da CPU: escolhe o arquétipo que mais cria chances contra o jeito de jogar do adversário, levando em conta o que o
 * elenco sabe fazer (meio-campo técnico para o passe curto, físico para a pressão, velocidade para o contra-ataque), o jeito
 * habitual do clube, a força relativa, o mando de campo e o placar. `ruido` (0..1) desloca um pouco a escolha para os técnicos
 * não serem idênticos.
 */
export function escolherArquetipo(clube: Clube, cond: CondicaoEquipe, condAdv: CondicaoEquipe, ctx: ContextoCpu, ruido: (a: Arquetipo) => number): Arquetipo {
  const habitual = arquetipoHabitual(clube);
  let melhor: Arquetipo = habitual;
  let notaMelhor = Number.NEGATIVE_INFINITY;
  for (const a of ARQUETIPOS) {
    let nota = vantagemTatica(a, cond, ctx.adversario, condAdv);
    if (a.id === habitual.id) nota += 0.12;
    if (a.id === ctx.atualId) nota += 0.06;
    // o que o elenco sabe fazer
    if (a.passe === 'curto') nota += 0.035 * limitar(cond.meio - 5.6, -2, 2);
    if (a.pressao === 'alta') nota -= 0.5 * Math.max(0, 0.88 - cond.forma);
    if (a.passe === 'longo' || a.id === 'contra') nota += 0.06 * limitar(cond.vel - 0.6, -0.3, 0.3) * 4 + 0.04 * (cond.aereoAtq - 0.5);
    // força relativa, mando de campo e placar
    const ord = ORDEM_ESTILO[a.estilo];
    if (ctx.diferencaForca < -0.5) nota += -0.05 * ord * Math.min(2, -ctx.diferencaForca);
    else if (ctx.diferencaForca > 0.5) nota += 0.04 * ord * Math.min(2, ctx.diferencaForca);
    if (ctx.casa) nota += 0.015 * ord;
    if (ctx.minuto > 0) {
      const tarde = limitar((ctx.minuto - 45) / 45, 0, 1);
      if (ctx.saldo < 0) nota += 0.07 * ord * (0.5 + tarde) * Math.min(2, -ctx.saldo);
      else if (ctx.saldo > 0) nota += -0.04 * ord * (0.5 + tarde) * Math.min(2, ctx.saldo);
    }
    nota += ruido(a);
    if (nota > notaMelhor) {
      notaMelhor = nota;
      melhor = a;
    }
  }
  return melhor;
}

export const perfilNeutro = (): PerfilTatico => ({ ...PERFIL_NEUTRO });

/** Funções que a CPU dá aos jogadores de um arquétipo: poucas, e só para quem tem o perfil (aptidão alta). */
export function papeisDaCpu(esc: Escalacao, a: Arquetipo): Record<number, Papel> {
  const slots = esc.titulares.map((j, i) => ({ j, slot: esc.slots?.[i] ?? j.posicao }));
  const preferidos: Record<string, Papel[]> = {
    posse: ['regista', 'construtor', 'lateral-invertido', 'mezala'],
    pressao: ['box-to-box', 'lateral-apoiador', 'ponta-construtor', 'atacante-interior'],
    contra: ['volante-fixo', 'rebatedor', 'extremo', 'oportunista'],
    direto: ['referencia', 'extremo', 'rebatedor', 'lateral-apoiador'],
    equilibrado: ['box-to-box', 'volante-fixo'],
    retranca: ['volante-fixo', 'rebatedor', 'libero', 'oportunista'],
    total: ['lateral-apoiador', 'box-to-box', 'atacante-interior', 'ponta-construtor'],
  };
  const lista = preferidos[a.id] ?? [];
  const out: Record<number, Papel> = {};
  const usados = new Set<number>();
  for (const papel of lista) {
    if (Object.keys(out).length >= MAX_PAPEIS_CPU) break;
    const pos = PAPEIS[papel].posicao;
    let melhor: Jogador | undefined;
    let nota = 0.95; // só dá a função a quem rende bem nela
    for (const s of slots) {
      if (s.slot !== pos || usados.has(s.j.id)) continue;
      const apt = aptidaoPapel(papel, s.j);
      if (apt > nota) {
        nota = apt;
        melhor = s.j;
      }
    }
    if (melhor) {
      out[melhor.id] = papel;
      usados.add(melhor.id);
    }
  }
  return out;
}
const MAX_PAPEIS_CPU = 3;

/** Descreve em poucas linhas o que a tática escolhida rende contra o jeito habitual do adversário (para a tela de escalação). */
export function leituraTatica(meu: PerfilTatico, cMeu: CondicaoEquipe, adv: Clube, escAdv: Escalacao): string[] {
  const arq = arquetipoHabitual(adv);
  const cAdv = condicaoDaEscalacao(escAdv);
  const linhas: string[] = [`${adv.nome} costuma apostar em ${arq.nome} (mentalidade ${ROTULO_ESTILO[arq.estilo].toLowerCase()}, passe ${arq.passe}, pressão ${arq.pressao}).`];
  const v = vantagemTatica(meu, cMeu, arq, cAdv);
  const pe = eficienciaPressao(cMeu.forma);
  if (arq.pressao === 'alta' && meu.passe === 'curto' && cMeu.meio < 6.2) linhas.push('Atenção: sua posse de bola sofre contra a pressão alta deles com este meio-campo. Passe longo ou funções que escapam da pressão (regista, zagueiro construtor) ajudam.');
  if (arq.pressao === 'alta' && meu.passe === 'longo') linhas.push('A bola longa nas costas da defesa deles tende a funcionar, principalmente com atacantes rápidos.');
  if (arq.pressao === 'baixa' && meu.passe === 'longo' && cMeu.aereoAtq < 0.6) linhas.push('Contra o bloco baixo deles, bola longa sem um homem de referência rende pouco.');
  if (arq.pressao === 'baixa' && meu.passe === 'curto') linhas.push('Você terá a bola, mas vão fechar os espaços: paciência e ataques pelo meio.');
  if (ORDEM_ESTILO[arq.estilo] >= 1 && ORDEM_ESTILO[meu.estilo] <= 0 && cMeu.vel > 0.62) linhas.push('Eles se expõem: seu time rápido pode ser letal no contra-ataque.');
  if (meu.pressao === 'alta' && pe < 0.7) linhas.push('Seu time está cansado para pressionar alto: a defesa pode desmoronar no fim do jogo.');
  linhas.push(v > 0.06 ? 'Leitura: sua tática leva vantagem neste confronto.' : v < -0.06 ? 'Leitura: o estilo deles encaixa melhor contra o seu.' : 'Leitura: confronto equilibrado.');
  return linhas;
}
